import React, { useCallback } from 'react';
import AntCondition from '../../utils/AntCondition';
import { formatDateShort, t } from './GanttUtils';

// ─── Task Panel (Left side) ─────────────────────────────────────
export default function GanttTaskPanel(props) {
    var {
        tasks, expandedGroups, onToggleGroup, onTaskClick, onTaskContextMenu,
        nameField, assigneeField, startField, endField, statusField,
        rowHeight, compact, selectedTaskId, session,
        columns: columnsProp, editable
    } = props;

    // Default columns if not configured
    var columns = columnsProp || [
        { field: 'name', label: { vi: 'Công việc', en: 'Task', fr: 'Tâche', es: 'Tarea', id: 'Tugas', ja: 'タスク', zh: '任务', ko: '작업', hi: 'कार्य', ar: 'مهمة' }, flex: 1 },
        { field: 'assignee', label: { vi: 'Phụ trách', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Petugas', ja: '担当', zh: '负责人', ko: '담당', hi: 'प्रभारी', ar: 'المسؤول' }, width: 80, hideCompact: true },
        { field: 'dates', label: { vi: 'Thời gian', en: 'Dates', fr: 'Période', es: 'Período', id: 'Periode', ja: '期間', zh: '期间', ko: '기간', hi: 'अवधि', ar: 'الفترة' }, width: 90, hideCompact: true }
    ];

    var visibleCols = compact ? columns.filter(function (c) { return !c.hideCompact; }) : columns;

    var handleContextMenu = useCallback(function (e, task) {
        e.preventDefault();
        e.stopPropagation();
        if (onTaskContextMenu) onTaskContextMenu(e, task);
    }, [onTaskContextMenu]);

    return (
        <div className="gantt-task-panel">
            {/* Header */}
            <div className="gantt-task-header" style={{ height: rowHeight }}>
                {visibleCols.map(function (col, ci) {
                    var style = {};
                    if (col.flex) style.flex = col.flex;
                    if (col.width) { style.width = col.width; style.flexShrink = 0; }
                    return (
                        <span key={ci} className="task-header-cell" style={style}>
                            {typeof col.label === 'object' ? t(col.label) : col.label}
                        </span>
                    );
                })}
            </div>

            {/* Task rows */}
            <div className="gantt-task-rows">
                {tasks.map(function (task) {
                    var level = task._level || 0;
                    var isGroup = task.type === 'group';
                    var isMilestone = task.type === 'milestone';
                    var hasChildren = task._hasChildren;
                    var name = task[nameField || 'name'] || task.name || '';
                    var assignee = task[assigneeField || 'assignee'] || '';
                    var startDate = task[startField || 'start'];
                    var endDate = task[endField || 'end'];
                    var expanded = expandedGroups[task.id] !== false;
                    var isSelected = selectedTaskId === task.id;
                    var status = task[statusField || 'status'] || '';

                    // Condition visibility per task (if task has visible config)
                    if (task.visible && session) {
                        var vis = AntCondition.checkAll(task, task.visible, session);
                        if (!vis) return null;
                    }

                    var cls = 'gantt-task-row';
                    if (isGroup) cls += ' group';
                    if (isMilestone) cls += ' milestone';
                    if (isSelected) cls += ' selected';

                    return (
                        <div
                            key={task.id}
                            className={cls}
                            style={{ height: rowHeight }}
                            onClick={function () { if (onTaskClick) onTaskClick(task); }}
                            onContextMenu={function (e) { handleContextMenu(e, task); }}
                            title={name + (assignee ? ' — ' + assignee : '') + (status ? ' [' + status + ']' : '')}
                        >
                            {visibleCols.map(function (col, ci) {
                                var style = {};
                                if (col.flex) style.flex = col.flex;
                                if (col.width) { style.width = col.width; style.flexShrink = 0; }

                                if (col.field === 'name' || ci === 0) {
                                    return (
                                        <div key={ci} className="task-name" style={{ ...style, paddingLeft: 10 + level * 18 }}>
                                            {(isGroup || hasChildren) && (
                                                <span
                                                    className={'task-toggle' + (expanded ? ' expanded' : '')}
                                                    onClick={function (e) {
                                                        e.stopPropagation();
                                                        if (onToggleGroup) onToggleGroup(task.id);
                                                    }}
                                                >
                                                    ▸
                                                </span>
                                            )}
                                            {isMilestone && <span className="task-milestone-icon">◆</span>}
                                            <span className={'task-label' + (isGroup ? ' bold' : '')}>{name}</span>
                                        </div>
                                    );
                                }

                                if (col.field === 'assignee') {
                                    return <span key={ci} className="task-cell" style={style}>{assignee}</span>;
                                }

                                if (col.field === 'dates') {
                                    return (
                                        <span key={ci} className="task-cell task-dates" style={style}>
                                            {startDate ? formatDateShort(new Date(startDate)) : ''}
                                            {startDate && endDate && startDate !== endDate ? ' → ' + formatDateShort(new Date(endDate)) : ''}
                                        </span>
                                    );
                                }

                                if (col.field === 'progress') {
                                    var pct = task[col.dataField || 'progress'] || 0;
                                    return (
                                        <span key={ci} className="task-cell" style={style}>
                                            <span className="task-progress-mini">
                                                <span className="task-progress-fill" style={{ width: pct + '%' }} />
                                            </span>
                                            <span className="task-progress-text">{pct}%</span>
                                        </span>
                                    );
                                }

                                if (col.field === 'status') {
                                    return <span key={ci} className="task-cell task-status-badge" style={style}>{status}</span>;
                                }

                                // Custom field
                                var val = task[col.dataField || col.field] || '';
                                return <span key={ci} className="task-cell" style={style}>{typeof val === 'object' ? t(val) : val}</span>;
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
